import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  on,
  untrack,
  type Accessor,
} from 'solid-js';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import { useAppContext } from '../AppContextProvider';
import { SidePanelTabName } from '../constants/panel-constants';
import type { SyncFxWorkspaceReferencesInput } from './fx-workspace-repository';

export interface FxWorkspaceDataEditingApi {
  readonly isWorkspaceDataEditing: Accessor<boolean>;
  readonly editingWorkspaceId: Accessor<string | undefined>;
  readonly isEditingWorkspaceReferencesLoaded: Accessor<boolean>;
  readonly draftReferences: Accessor<ReadonlySet<string>>;
  enterWorkspaceDataEditing(): void;
  toggleDraftReference(refTrn: string): void;
  isDraftReference(refTrn: string): boolean;
  setDraftReferenceSelection(
    refTrns: readonly string[],
    selected: boolean
  ): void;
  commitWorkspaceDataEditing(): Promise<boolean>;
  cancelWorkspaceDataEditing(): void;
  resetSession(): void;
}

function mergeLoadedReferencesIntoDraft(
  draft: ReadonlySet<string>,
  loadedReferences: ReadonlySet<string>
): Set<string> {
  const next = new Set(draft);
  for (const refTrn of loadedReferences) {
    next.add(refTrn);
  }
  return next;
}

export function useFxWorkspaceDataEditing(options: {
  activeWorkspaceId: Accessor<string | undefined>;
  getWorkspaceReferences(workspaceId: string): ReadonlySet<string>;
  areWorkspaceReferencesLoaded(workspaceId: string): boolean;
  syncWorkspaceReferences(
    workspaceId: string,
    input: SyncFxWorkspaceReferencesInput
  ): Promise<boolean>;
}): FxWorkspaceDataEditingApi {
  const appContext = useAppContext();
  const [isWorkspaceDataEditing, setIsWorkspaceDataEditing] =
    createSignal(false);
  const [editingWorkspaceId, setEditingWorkspaceId] = createSignal<
    string | undefined
  >(undefined);
  const [draftReferences, setDraftReferences] = createSignal<
    ReadonlySet<string>
  >(new Set<string>());
  const [isDraftBaselineMerged, setIsDraftBaselineMerged] = createSignal(false);

  const isEditingWorkspaceReferencesLoaded = createMemo((): boolean => {
    const workspaceId = editingWorkspaceId();
    return (
      workspaceId !== undefined &&
      options.areWorkspaceReferencesLoaded(workspaceId)
    );
  });

  const resetSession = (): void => {
    batch(() => {
      setIsWorkspaceDataEditing(false);
      setEditingWorkspaceId(undefined);
      setDraftReferences(new Set<string>());
      setIsDraftBaselineMerged(false);
    });
  };

  const exitEditingAndReturnToWorkspaces = (): void => {
    resetSession();
    appContext.setSelectedSidePanelTab(SidePanelTabName.Workspaces);
  };

  const mergeLoadedReferencesBaseline = (workspaceId: string): void => {
    if (
      !options.areWorkspaceReferencesLoaded(workspaceId) ||
      isDraftBaselineMerged()
    ) {
      return;
    }

    batch(() => {
      setDraftReferences((previous) =>
        mergeLoadedReferencesIntoDraft(
          previous,
          options.getWorkspaceReferences(workspaceId)
        )
      );
      setIsDraftBaselineMerged(true);
    });
  };

  createEffect(
    on(
      () => options.activeWorkspaceId(),
      (activeId) => {
        if (!untrack(isWorkspaceDataEditing)) {
          return;
        }

        if (untrack(editingWorkspaceId) !== activeId) {
          exitEditingAndReturnToWorkspaces();
        }
      }
    )
  );

  createEffect(
    on(
      () => {
        const workspaceId = editingWorkspaceId();
        return {
          isEditing: isWorkspaceDataEditing(),
          workspaceId,
          areReferencesLoaded:
            workspaceId !== undefined &&
            options.areWorkspaceReferencesLoaded(workspaceId),
        };
      },
      ({ isEditing, workspaceId, areReferencesLoaded }) => {
        if (!isEditing || workspaceId === undefined || !areReferencesLoaded) {
          return;
        }

        mergeLoadedReferencesBaseline(workspaceId);
      }
    )
  );

  const enterWorkspaceDataEditing = (): void => {
    const workspaceId = options.activeWorkspaceId();
    if (workspaceId === undefined) {
      return;
    }

    batch(() => {
      if (!isWorkspaceDataEditing() || editingWorkspaceId() !== workspaceId) {
        setIsDraftBaselineMerged(false);
        if (options.areWorkspaceReferencesLoaded(workspaceId)) {
          setDraftReferences(
            new Set(options.getWorkspaceReferences(workspaceId))
          );
          setIsDraftBaselineMerged(true);
        } else {
          setDraftReferences(new Set<string>());
        }
        setEditingWorkspaceId(workspaceId);
        setIsWorkspaceDataEditing(true);
      }

      appContext.setSelectedSidePanelTab(SidePanelTabName.ProjectData);
    });
  };

  const toggleDraftReference = (refTrn: string): void => {
    if (!isWorkspaceDataEditing()) {
      return;
    }

    setDraftReferences((previous) => {
      const next = new Set(previous);
      if (next.has(refTrn)) {
        next.delete(refTrn);
      } else {
        next.add(refTrn);
      }
      return next;
    });
  };

  const isDraftReference = (refTrn: string): boolean =>
    draftReferences().has(refTrn);

  const setDraftReferenceSelection = (
    refTrns: readonly string[],
    selected: boolean
  ): void => {
    if (!isWorkspaceDataEditing()) {
      return;
    }

    setDraftReferences((previous) => {
      const next = new Set(previous);
      for (const refTrn of refTrns) {
        if (selected) {
          next.add(refTrn);
        } else {
          next.delete(refTrn);
        }
      }
      return next;
    });
  };

  const commitWorkspaceDataEditing = async (): Promise<boolean> => {
    const workspaceId = editingWorkspaceId();
    if (!isWorkspaceDataEditing() || workspaceId === undefined) {
      return false;
    }

    if (!options.areWorkspaceReferencesLoaded(workspaceId)) {
      NotificationEventBus.emit({
        type: 'error',
        message: t('mobileMapping.fxWorkspace.workspaceDataEditing.notReady'),
      });
      return false;
    }

    mergeLoadedReferencesBaseline(workspaceId);

    const current = options.getWorkspaceReferences(workspaceId);
    const draft = draftReferences();
    const toAdd = [...draft].filter((refTrn) => !current.has(refTrn));
    const toRemove = [...current].filter((refTrn) => !draft.has(refTrn));

    const saved = await options.syncWorkspaceReferences(workspaceId, {
      toAdd,
      toRemove,
    });
    if (saved) {
      exitEditingAndReturnToWorkspaces();
    }

    return saved;
  };

  const cancelWorkspaceDataEditing = (): void => {
    if (!isWorkspaceDataEditing()) {
      return;
    }

    exitEditingAndReturnToWorkspaces();
  };

  return {
    isWorkspaceDataEditing,
    editingWorkspaceId,
    isEditingWorkspaceReferencesLoaded,
    draftReferences,
    enterWorkspaceDataEditing,
    toggleDraftReference,
    isDraftReference,
    setDraftReferenceSelection,
    commitWorkspaceDataEditing,
    cancelWorkspaceDataEditing,
    resetSession,
  };
}
